import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Beer, Bone, Sparkles, Zap, Instagram, Globe } from "lucide-react";
import { api } from "../lib/api";
import { KILTRO_ASSETS } from "../lib/assets";
import VenueCard from "../components/VenueCard";
import ActivityFeed from "../components/ActivityFeed";
import KiltroDogIcon from "../components/home/KiltroDogIcon";
import Pillar from "../components/home/Pillar";
import Stat from "../components/home/Stat";

export default function Home() {
  const { t } = useTranslation();
  const [venues, setVenues] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.listVenues({ featured: true }).then(setVenues).catch(() => {});
    api.stats().then(setStats).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — Beer glass on pure black. Clean, premium. */}
      <section className="relative overflow-hidden border-b-2 border-[#1A1A1A] bg-black">
        {/* Beer glass background — contain shows the whole glass (foam + base) */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${KILTRO_ASSETS.heroBar})`,
            backgroundSize: "contain",
            backgroundPosition: "right center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#000000",
          }}
        />
        {/* Subtle left gradient to deepen black + guarantee text contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-14 pb-24 lg:pb-28">
          <div className="flex items-center gap-4 mb-6">
            <img
              src={KILTRO_ASSETS.logo}
              alt="Kiltro"
              className="w-64 h-64 object-contain"
              data-testid="hero-kiltro-logo"
            />
          </div>

          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight leading-[1.15] max-w-2xl">
            {t("home.heroTitle1")}{" "}
            <span className="inline-block">
              <span className="text-white">Kiltro</span> <span className="text-[#FEBA01]">Scout</span>,
            </span>{" "}
            <span className="inline-block bg-[#FEBA01] text-black px-2 py-0.5 -skew-x-[6deg] shadow-[3px_3px_0_0_rgba(0,0,0,0.6)]">
              {t("home.heroTitle2")}
            </span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-[#E5E5E5] leading-relaxed font-medium">
            {t("home.heroDesc")}
          </p>

          {stats && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              <Stat label={t("home.statsLocales")} value={stats.venues} />
              <Stat label={t("home.statsCoupons")} value={stats.coupons} />
              <Stat label={t("home.statsRoutes")} value={stats.routes} />
              <Stat label={t("home.statsCheckins")} value={stats.checkins} />
            </div>
          )}

          {/* Stamp seal vintage — SVG inline, sin fondo cuadrado.
              Posicionado a la derecha del badge BETA, en la zona negra (NO sobre headline ni vaso). */}
        </div>

        <div className="kiltro-stripes h-3" />
      </section>

      {/* SOCIAL STRIPE — visible Instagram + web */}
      <section className="bg-black border-b-2 border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-8 text-center">
          <a
            href="https://instagram.com/kiltroscout"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[#FEBA01] font-mono text-xs uppercase tracking-[0.2em] hover:text-white transition-colors"
            data-testid="home-ig-link"
          >
            <Instagram size={14} /> @kiltroscout
          </a>
          <span className="hidden sm:inline text-[#FEBA01]/40">·</span>
          <a
            href="https://www.kiltroscout.cl"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[#FEBA01] font-mono text-xs uppercase tracking-[0.2em] hover:text-white transition-colors"
            data-testid="home-web-link"
          >
            <Globe size={14} /> www.kiltroscout.cl
          </a>
        </div>
      </section>

      {/* FEATURE CARDS — todos estilo cupón amarillo */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-3">
          <Pillar
            to="/descubrir"
            icon={<Globe size={62} strokeWidth={1.5} />}
            title={t("home.pillarDiscoverTitle")}
            desc={t("home.pillarDiscoverDesc")}
            testid="home-pillar-descubre"
          />
          <Pillar
            to="/rutas"
            icon={<KiltroDogIcon />}
            title={t("home.pillarRouteTitle")}
            desc={t("home.pillarRouteDesc")}
            testid="home-pillar-recorre"
          />
          <Pillar
            to="/cupones"
            icon={<Bone size={62} strokeWidth={1.8} />}
            title={t("home.pillarCouponTitle")}
            desc={t("home.pillarCouponDesc")}
            testid="home-pillar-encuentra"
          />
        </div>
      </section>

      {/* FEATURED VENUES */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01] flex items-center gap-1">
              <Sparkles size={12} /> Top Kiltro
            </div>
            <h2 className="font-display text-4xl uppercase tracking-tight">Locales destacados</h2>
          </div>
          <Link to="/descubrir" className="font-display uppercase tracking-wider text-sm text-[#FEBA01] hover:underline" data-testid="home-see-all-venues">
            Ver todo →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {venues.slice(0, 6).map((v) => (
            <VenueCard key={v.id} venue={v} />
          ))}
        </div>
      </section>

      {/* CTA STRIPE — black bg + yellow text for max contrast */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="bg-black border-2 border-[#FEBA01] text-[#FEBA01] p-8 lg:p-12 shadow-brutal relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-10 mix-blend-screen pointer-events-none"
            style={{
              backgroundImage: `url(${KILTRO_ASSETS.brushstroke})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("home.haveBar")}</div>
              <h3 className="mt-1 font-display text-4xl lg:text-5xl uppercase leading-none tracking-tight text-[#FEBA01]">{t("home.joinPack")}</h3>
              <p className="mt-3 max-w-2xl text-base font-medium text-[#F5F5F5]">
                {t("home.joinDesc")}
              </p>
            </div>
            <Link
              to="/locales"
              className="btn-brutal bg-[#FEBA01] text-black font-display text-lg uppercase tracking-wide px-6 py-3 inline-flex items-center gap-2 shadow-brutal-white whitespace-nowrap"
              data-testid="home-business-cta"
            >
              {t("home.joinCta")} <Beer size={18} />
            </Link>
          </div>
        </div>
      </section>

      <ActivityFeed limit={8} />
    </div>
  );
}
